/**
 * PII Scrubber — ADR-009 §9.7
 *
 * Função pura que remove dados sensíveis de qualquer payload antes de
 * chegar aos logs externos (Vercel, Sentry). Única porta de saída para PII.
 *
 * Regras (casos canônicos do SPEC Bloco 10):
 *   - email       : mascara local-part, preserva domínio
 *   - phone       : mascara dígitos de telefone
 *   - IP v4/v6    : agrega para /24 (v4) ou /64 (v6)
 *   - journal.*   : redacted por completo (diário é 100% privado)
 *   - password/token/secret/authorization: redacted integralmente
 *   - payload aninhado: recursivo
 *
 * Nunca muta a entrada. Retorna cópia com valores scrubbed.
 */

/** Campos cujo conteúdo deve ser integralmente redacted (nomes de chave). */
const REDACTED_KEYS = new Set([
  'password',
  'passwd',
  'token',
  'access_token',
  'refresh_token',
  'id_token',
  'api_key',
  'apikey',
  'secret',
  'client_secret',
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
]);

const REDACTED_PLACEHOLDER = '[REDACTED]';
// Placeholder intencionalmente evita a palavra "private" para não conflitar com
// asserts de testes que checam ausência de conteúdo sensível no output.
const JOURNAL_PLACEHOLDER = '[REDACTED journal content]';

// Regex canônicos
const EMAIL_REGEX = /([a-z0-9._%+-]+)@([a-z0-9.-]+\.[a-z]{2,})/gi;
const IPV4_REGEX = /\b((?:\d{1,3}\.){3})\d{1,3}\b/g;
const PHONE_REGEX = /(\+?\d[\d\s().-]{6,}\d)/g;

function maskEmail(email: string): string {
  const m = email.match(/^([^@]+)@(.+)$/);
  if (!m) return REDACTED_PLACEHOLDER;
  const local = m[1] ?? '';
  const domain = m[2] ?? '';
  if (local.length <= 1) return `*@${domain}`;
  return `${local[0]}***@${domain}`;
}

function maskIPv4(ip: string): string {
  const parts = ip.split('.');
  if (parts.length !== 4) return ip;
  return `${parts[0]}.${parts[1]}.${parts[2]}.0/24`;
}

function maskIPv6(ip: string): string {
  // Compressed or expanded; take first 4 groups (~/64) and truncate.
  const parts = ip.split(':');
  const kept = parts.slice(0, 4).filter(Boolean);
  return `${kept.join(':')}::/64`;
}

function isLikelyIPv6(value: string): boolean {
  return value.includes(':') && /^[0-9a-f:]+$/i.test(value);
}

function scrubString(s: string): string {
  let out = s;

  // Order matters: email first (contains digits sometimes), then phone, then IPv4.
  out = out.replace(EMAIL_REGEX, (m) => maskEmail(m));

  // IPv4 in free text
  out = out.replace(IPV4_REGEX, (match) => maskIPv4(match));

  // Phone in free text (after email to avoid eating digits in email-local-parts)
  out = out.replace(PHONE_REGEX, (match) => {
    // Preserve country code prefix if starts with + and keep some digits redacted.
    const digits = match.replace(/\D/g, '');
    if (digits.length < 7) return match;
    return match.startsWith('+')
      ? `+${digits.slice(0, 2)}${'*'.repeat(Math.max(0, digits.length - 2))}`
      : '*'.repeat(digits.length);
  });

  return out;
}

/**
 * Scrubs PII from arbitrary input, recursively.
 * Does NOT mutate the input.
 */
export function scrubPII(value: unknown): unknown {
  return scrubValue(value, /* insideJournal */ false, /* keyHint */ undefined);
}

function scrubValue(value: unknown, insideJournal: boolean, keyHint: string | undefined): unknown {
  if (value === null || value === undefined) return value;

  if (typeof value === 'string') {
    if (insideJournal) return JOURNAL_PLACEHOLDER;

    // Key-based redaction
    if (keyHint && REDACTED_KEYS.has(keyHint.toLowerCase())) {
      return REDACTED_PLACEHOLDER;
    }

    // IP field — key-based
    if (keyHint?.toLowerCase() === 'ip') {
      if (isLikelyIPv6(value)) return maskIPv6(value);
      if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(value)) return maskIPv4(value);
    }

    // Phone field — key-based
    if (keyHint?.toLowerCase() === 'phone') {
      const digits = value.replace(/\D/g, '');
      if (digits.length === 0) return value;
      return value.startsWith('+')
        ? `+${digits.slice(0, 2)}${'*'.repeat(Math.max(0, digits.length - 2))}`
        : '*'.repeat(digits.length);
    }

    // Email field — key-based
    if (keyHint?.toLowerCase() === 'email') {
      const maybe = value.match(/^([^@]+)@(.+)$/);
      if (maybe) return maskEmail(value);
    }

    // Free-text scrub pass
    return scrubString(value);
  }

  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((v) => scrubValue(v, insideJournal, keyHint));
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      const lowerKey = k.toLowerCase();
      if (lowerKey === 'journal') {
        out[k] = JOURNAL_PLACEHOLDER;
        continue;
      }
      if (REDACTED_KEYS.has(lowerKey)) {
        out[k] = REDACTED_PLACEHOLDER;
        continue;
      }
      out[k] = scrubValue(v, insideJournal || lowerKey === 'journal', k);
    }
    return out;
  }

  return value;
}
